import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Form, { FormField } from "src/components/ui/Form";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { genderOptions } from "src/constants/common";
import { toast } from "react-toastify";

interface UserLocationState {
  user?: any;
}

const UserForm: React.FC = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [requestedList, setRequestedList] = useState(false);

  const dispatch = useAppDispatch();
  const { createTenantUser, editTenantUser, tenantUserList } = useAppSelector(
    (state) => state.tenantUser
  );
  const { tenantRoleList } = useAppSelector((state) => state.tenant);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const locationState = location.state as UserLocationState | undefined;
  const userFromState = locationState?.user || null;

  const userFromList = useMemo(() => {
    if (!userId) return null;
    return (
      tenantUserList?.data?.data?.find((item) => item?._id === userId) || null
    );
  }, [userId, tenantUserList?.data?.data]);

  const activeUser = userFromState || userFromList;

  const normalizedInitialState = useMemo(() => {
    if (!activeUser) return {};
    const roleId =
      typeof activeUser.role === "string"
        ? activeUser.role
        : activeUser.role?._id || "";
    return {
      ...activeUser,
      role: roleId,
      password: "",
    };
  }, [activeUser]);

  useEffect(() => {
    dispatch(tenantActions.getTenantRoleListRequest({}));
  }, [dispatch]);

  useEffect(() => {
    if (userId && !activeUser && !requestedList) {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page: 1,
          limit: 1000,
        })
      );
      setRequestedList(true);
    }
  }, [userId, activeUser, requestedList, dispatch]);

  useEffect(() => {
    if (userId && requestedList && !activeUser && tenantUserList?.data) {
      toast.error("User not found");
      navigate("/app/user/list");
    }
  }, [userId, requestedList, activeUser, tenantUserList?.data, navigate]);

  useEffect(() => {
    if (createTenantUser.data) {
      toast.success("User created successfully");
      handleClose();
    } else if (createTenantUser.error) {
      toast.error(`Error: ${createTenantUser.error}`);
    }
  }, [createTenantUser]);

  useEffect(() => {
    if (editTenantUser.data) {
      toast.success("User modified successfully");
      handleClose();
    } else if (editTenantUser.error) {
      toast.error(`Error: ${editTenantUser.error}`);
    }
  }, [editTenantUser]);

  const handleClose = () => {
    dispatch(tenantUserActions.resetCreateTenantUser());
    dispatch(tenantUserActions.resetEditTenantUser());
    navigate("/app/user/list");
  };

  const fields: FormField[] = useMemo(
    () => [
      {
        label: "First Name",
        name: "firstName",
        type: "text",
        placeholder: "Enter first name",
        validations: { required: true },
      },
      {
        label: "Last Name",
        name: "lastName",
        type: "text",
        placeholder: "Enter last name",
        validations: { required: true },
      },
      {
        label: "Email",
        name: "email",
        type: "text",
        placeholder: "Enter email address",
        validations: {
          required: true,
          regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
          message: "Invalid email format",
        },
      },
      {
        label: "Password",
        name: "password",
        type: "password",
        placeholder: "Enter password",
        validations: userId ? { required: false, optional: true } : { required: true },
      },
      {
        label: "Date of Birth",
        name: "dob",
        type: "text",
        placeholder: "dd/mm/yyyy",
        validations: { required: true },
      },
      {
        label: "Gender",
        name: "gender",
        type: "dropdown",
        options: genderOptions,
        validations: { required: true },
      },
      {
        label: "Role",
        name: "role",
        type: "dropdown",
        options:
          tenantRoleList?.data?.data?.map((tenantRole: any) => ({
            label: tenantRole?.name || "",
            value: tenantRole?._id || "",
          })) || [],
        validations: { required: true },
      },
    ],
    [tenantRoleList?.data?.data, userId]
  );

  const handleSubmit = (data: Record<string, any>) => {
    if (userId) {
      const payload = { ...data };
      if (!payload.password) {
        delete payload.password;
      }
      dispatch(
        tenantUserActions.editTenantUserRequest({
          ...(payload as any),
          _id: userId,
        })
      );
      return;
    }

    dispatch(
      tenantUserActions.createTenantUserRequest({
        ...(data as any),
      })
    );
  };

  const title = userId ? "Edit User" : "Create User";

  return (
    <DashboardLayout
      meta={{
        title: `${title} | Invyntra`,
        description: "Manage tenant users in your workspace.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={title}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Users", path: "/app/user/list" },
            { label: title, path: userId ? `/app/user/${userId}/edit` : "/app/user/create" },
          ]}
        />

        <Card>
          <Form
            initialState={normalizedInitialState as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitLabel={userId ? "Update User" : "Create User"}
            loading={createTenantUser.loading || editTenantUser.loading}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default UserForm;
